import React, { useContext, useState } from "react";
import { AuthContext } from "../state/AuthContext";
import SpeechInput from "./SpeechInput";

export default function AddFollowupModal({ enquiry, onClose, onSaved }) {
  const { http } = useContext(AuthContext);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [progressNote, setProgressNote] = useState("");
  const [status, setStatus] = useState("");
  const [nextFollowupAt, setNextFollowupAt] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);

  const onAttachmentsSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPendingFiles((current) => [
      ...current,
      ...files.map((f) => ({
        file: f,
        name: f.name,
        size: f.size,
        id: `${Date.now()}-${Math.random()}`,
      })),
    ]);
    e.target.value = "";
  };

  const removePendingFile = (id) => {
    setPendingFiles((current) => current.filter((f) => f.id !== id));
  };

  const formatSize = (bytes) => {
    if (!bytes) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!progressNote.trim()) {
      setError("Please enter a progress note.");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload = new FormData();
      payload.append("progressNote", progressNote);
      if (nextFollowupAt) payload.append("nextFollowupAt", nextFollowupAt);
      if (status) payload.append("status", status);
      pendingFiles.forEach((pf) => payload.append("documents", pf.file));

      await http.post(`/api/enquiries/${enquiry.id}/followups`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (onSaved) onSaved();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to save followup progress.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
    >
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content shadow">
          <div className="modal-header bg-dark text-white">
            <h5 className="modal-title">
              📝 Add Followup - {enquiry?.applicationNo} ({enquiry?.customerName})
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>

          <div className="modal-body">
            {error ? <div className="alert alert-danger">{error}</div> : null}

            <form onSubmit={handleSubmit} className="row g-3">
              <div className="col-12">
                <label className="form-label fw-bold">Progress Note *</label>
                <SpeechInput
                  as="textarea"
                  name="progressNote"
                  value={progressNote}
                  onChange={(e) => setProgressNote(e.target.value)}
                  rows={4}
                  placeholder="Enter work progress details, customer update, or notes..."
                  required
                />
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label fw-bold">Update Status</label>
                <select
                  className="form-select"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  <option value="">Keep current status ({enquiry?.status})</option>
                  <option value="IN_PROGRESS">IN_PROGRESS</option>
                  <option value="COMPLETED">COMPLETED</option>
                  <option value="CLOSED">CLOSED</option>
                </select>
              </div>

              <div className="col-12 col-md-6">
                <label className="form-label fw-bold">Next Followup / Appointment Date</label>
                <input
                  type="datetime-local"
                  className="form-control"
                  value={nextFollowupAt}
                  onChange={(e) => setNextFollowupAt(e.target.value)}
                />
              </div>

              <div className="col-12">
                <div className="card border-0 bg-light">
                  <div className="card-body p-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <div className="small fw-semibold">Attachments / Documents</div>
                      <label className="btn btn-sm btn-outline-primary mb-0">
                        📎 Choose Files
                        <input
                          type="file"
                          className="d-none"
                          multiple
                          onChange={onAttachmentsSelected}
                        />
                      </label>
                    </div>

                    {pendingFiles.length === 0 ? (
                      <div className="small text-muted">No files selected.</div>
                    ) : (
                      <ul className="list-group list-group-flush mb-0">
                        {pendingFiles.map((pf) => (
                          <li
                            key={pf.id}
                            className="list-group-item bg-transparent px-0 d-flex justify-content-between align-items-center"
                          >
                            <span className="small text-truncate" style={{ maxWidth: "300px" }}>
                              📄 {pf.name} ({formatSize(pf.size)})
                            </span>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removePendingFile(pf.id)}
                            >
                              Remove
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-12 text-end mt-4">
                <button
                  type="button"
                  className="btn btn-outline-secondary me-2"
                  onClick={onClose}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-success" disabled={saving}>
                  {saving ? "Saving Followup..." : "💾 Save Followup Progress"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
